import React from 'react';
import PropTypes from 'prop-types';
import { Dropdown, Icon } from 'semantic-ui-react';
import SettingsUtils from '../SettingsUtils';
import { isMobileOnly } from 'react-device-detect';
import { firestore } from '../firebaseConfig';
import { applyResolvedPlaceToAddress } from '../utils/addressSelection';
import {
  fetchDocketAutocomplete,
  fetchDocketPlaceResolved,
  fetchDocketZipGeocode,
  isDocketGeoConfigured,
} from '../utils/docketGeoClient';
import { getUsStateAbbreviation } from '../utils/usStateAbbreviation';
let $ = window.$;

class AddressForm extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      address: this.props.entity,
      addressSuggestions: [],
      suggestionsOpen: false,
      suggestionsLoading: false,
      suggestionHighlight: -1,
      countiesByState: {},
      countyOptions: [],
    };
    this.autocompleteTimer = null;
    this.autocompleteController = null;
    this.blurTimer = null;
  }

  componentDidUpdate(previousProps) {
    let _this = this;
    $(`#${this.props.id} .stateDropdown`).dropdown({
      onChange: function (value, text, $selectedItem) {
        _this.updateState(value);
      },
    });

    let address = undefined;
    if (this.state.address !== undefined) {
      address = JSON.parse(JSON.stringify(this.state.address));
    }
    let changed = false;
    if (
      (previousProps.entity === undefined || previousProps.entity.address === undefined) &&
      this.props.entity !== undefined &&
      this.props.entity.address !== undefined
    ) {
      changed = true;
      address = this.props.entity.address;
    } else if (
      previousProps.entity !== undefined &&
      previousProps.entity.address !== undefined &&
      this.props.entity !== undefined &&
      this.props.entity.address === undefined
    ) {
      changed = true;
      address = this.props.entity.address;
    } else if (address !== undefined && this.props.entity !== undefined && this.props.entity.address !== undefined) {
      if (address.addressLine1 !== this.props.entity.address.addressLine1) {
        changed = true;
        address.addressLine1 = this.props.entity.address.addressLine1;
      }
      if (address.addressLine2 !== this.props.entity.address.addressLine2) {
        changed = true;
        address.addressLine2 = this.props.entity.address.addressLine2;
      }
      if (address.city !== this.props.entity.address.city) {
        changed = true;
        address.city = this.props.entity.address.city;
      }
      if (address.state !== this.props.entity.address.state) {
        changed = true;
        address.state = this.props.entity.address.state;
      }
      if (address.zip !== this.props.entity.address.zip) {
        changed = true;
        address.zip = this.props.entity.address.zip;
      }
      if (address.county !== this.props.entity.address.county) {
        changed = true;
        address.county = this.props.entity.address.county;
      }
      if (address.countyVerbose !== this.props.entity.address.countyVerbose) {
        changed = true;
        address.countyVerbose = this.props.entity.address.countyVerbose;
      }
      if (JSON.stringify(address.location) !== JSON.stringify(this.props.entity.address.location)) {
        changed = true;
        address.location = this.props.entity.address.location;
      }
    }

    if (changed) {
      const addrAfter = address;
      const cc = SettingsUtils.getCountryCode(this.props.company);
      this.setState(
        {
          address,
          addressSuggestions: [],
          suggestionsOpen: false,
          suggestionsLoading: false,
          suggestionHighlight: -1,
        },
        () => {
          if (cc === 'US' && addrAfter && addrAfter.state) {
            this.fetchCountiesForStateName(addrAfter.state);
          } else if (cc !== 'US') {
            this.setState({ countyOptions: [] });
          }
        },
      );
    }
  }

  componentDidMount() {
    this.addressChanged();
    const cc = SettingsUtils.getCountryCode(this.props.company);
    const st = this.state.address && this.state.address.state;
    if (cc === 'US' && st) {
      this.fetchCountiesForStateName(st);
    }
  }

  fetchCountiesForStateName(stateFullName) {
    const abbr = getUsStateAbbreviation(stateFullName);
    if (!abbr) {
      this.setState({ countyOptions: [] });
      return Promise.resolve();
    }
    const cached = this.state.countiesByState[abbr];
    if (Object.prototype.hasOwnProperty.call(this.state.countiesByState, abbr)) {
      this.setState({ countyOptions: cached });
      return Promise.resolve();
    }
    const _this = this;
    return firestore
      .doc(`regionalLookup/${abbr}`)
      .get()
      .then(function (snap) {
        const stateData = snap.data();
        const raw = stateData && Array.isArray(stateData.counties) ? stateData.counties : [];
        const sorted = raw.slice().sort(function (a, b) {
          return String(a).localeCompare(String(b));
        });
        const opts = sorted.map(function (c) {
          return { key: c, text: c, value: c };
        });
        _this.setState(function (prev) {
          return {
            countiesByState: Object.assign({}, prev.countiesByState, { [abbr]: opts }),
            countyOptions: opts,
          };
        });
      })
      .catch(function (e) {
        console.error(e);
        _this.setState({ countyOptions: [] });
      });
  }

  componentWillUnmount() {
    if (this.autocompleteTimer) {
      clearTimeout(this.autocompleteTimer);
    }
    if (this.blurTimer) {
      clearTimeout(this.blurTimer);
    }
    if (this.autocompleteController) {
      this.autocompleteController.abort();
    }
  }

  scheduleAddressAutocomplete(line1) {
    if (!isDocketGeoConfigured()) {
      this.setState({
        addressSuggestions: [],
        suggestionsOpen: false,
        suggestionsLoading: false,
        suggestionHighlight: -1,
      });
      return;
    }
    if (this.autocompleteTimer) {
      clearTimeout(this.autocompleteTimer);
    }
    if (this.autocompleteController) {
      this.autocompleteController.abort();
    }

    if (!line1 || line1.trim().length < 3) {
      this.setState({
        addressSuggestions: [],
        suggestionsOpen: false,
        suggestionsLoading: false,
        suggestionHighlight: -1,
      });
      return;
    }

    this.autocompleteTimer = setTimeout(() => {
      this.autocompleteController = new AbortController();
      const signal = this.autocompleteController.signal;
      this.setState({ suggestionsLoading: true });
      const company = this.props.company;
      const bias = this.props.biasLocation;
      fetchDocketAutocomplete(line1, company, bias, signal)
        .then((predictions) => {
          if (signal.aborted) {
            return;
          }
          this.setState({
            addressSuggestions: predictions,
            suggestionsOpen: predictions.length > 0,
            suggestionsLoading: false,
            suggestionHighlight: -1,
          });
        })
        .catch((err) => {
          if (err.code === 'ERR_CANCELED' || err.name === 'CanceledError') {
            return;
          }
          this.setState({
            suggestionsLoading: false,
            addressSuggestions: [],
            suggestionsOpen: false,
          });
        });
    }, 300);
  }

  handleAddress1Focus() {
    if (this.blurTimer) {
      clearTimeout(this.blurTimer);
    }
    if (this.state.addressSuggestions.length > 0) {
      this.setState({ suggestionsOpen: true });
    }
  }

  handleAddress1Blur() {
    this.blurTimer = setTimeout(() => {
      this.setState({ suggestionsOpen: false, suggestionHighlight: -1 });
    }, 200);
  }

  handleStreetKeyDown(e) {
    const { suggestionsOpen, addressSuggestions, suggestionHighlight } = this.state;
    if (!suggestionsOpen || addressSuggestions.length === 0) {
      if (e.key === 'Escape') {
        this.setState({ suggestionsOpen: false, suggestionHighlight: -1 });
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const next = Math.min(suggestionHighlight + 1, addressSuggestions.length - 1);
      this.setState({ suggestionHighlight: next });
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const next = Math.max(suggestionHighlight - 1, 0);
      this.setState({ suggestionHighlight: next });
    } else if (e.key === 'Enter') {
      if (suggestionHighlight >= 0) {
        e.preventDefault();
        this.handleSelectPrediction(addressSuggestions[suggestionHighlight]);
      }
    } else if (e.key === 'Escape') {
      this.setState({ suggestionsOpen: false, suggestionHighlight: -1 });
    }
  }

  handleSelectPrediction(prediction) {
    if (this.autocompleteTimer) {
      clearTimeout(this.autocompleteTimer);
    }
    if (this.autocompleteController) {
      this.autocompleteController.abort();
    }
    if (this.blurTimer) {
      clearTimeout(this.blurTimer);
    }
    const _this = this;
    fetchDocketPlaceResolved(prediction.place_id, this.props.company, undefined)
      .then(function (resolved) {
        const countryCode = SettingsUtils.getCountryCode(_this.props.company);
        const merged = applyResolvedPlaceToAddress(resolved, prediction, countryCode);
        if (!merged) {
          return;
        }
        let nextAddress = _this.getAddressClone();
        nextAddress.addressLine1 = merged.addressLine1;
        nextAddress.city = merged.city;
        nextAddress.state = merged.state;
        if (merged.zip !== undefined && merged.zip !== null && merged.zip !== '') {
          nextAddress.zip = merged.zip;
        }
        nextAddress.county = merged.county;
        nextAddress.countyVerbose = merged.countyVerbose;
        if (merged.location) {
          nextAddress.location = merged.location;
        }
        _this.setState(
          {
            address: nextAddress,
            addressSuggestions: [],
            suggestionsOpen: false,
            suggestionsLoading: false,
            suggestionHighlight: -1,
          },
          function () {
            _this.addressChanged(nextAddress);
            if (merged.state !== undefined && merged.state !== null && merged.state !== '') {
              $(`#${_this.props.id} .stateDropdown`).dropdown('set selected', merged.state, undefined);
            }
            if (SettingsUtils.getCountryCode(_this.props.company) === 'US' && merged.state) {
              _this.fetchCountiesForStateName(merged.state);
            }
          },
        );
      })
      .catch(function () {});
  }

  updateAddressLine1(line1) {
    let address = this.getAddressClone();
    address.addressLine1 = line1;

    this.setState({ address: address });
    this.addressChanged(address);
    this.scheduleAddressAutocomplete(line1);
  }

  updateAddressLine2(line2) {
    let address = this.getAddressClone();
    address.addressLine2 = line2;

    this.setState({ address: address });
    this.addressChanged(address);
  }

  updateLocation(location) {
    let address = this.getAddressClone();
    address.location = {
      latitude: location.lat,
      longitude: location.lng,
    };

    this.setState({ address: address });
    this.addressChanged(address);
  }

  updateCity(city) {
    let address = this.getAddressClone();
    address.city = city;

    this.setState({ address: address });
    this.addressChanged(address);
  }

  updateCounty(county, countyVerbose) {
    let address = this.getAddressClone();
    address.county = county;
    address.countyVerbose = countyVerbose;

    this.setState({ address: address });
    this.addressChanged(address);
  }

  updateState(state) {
    let address = this.getAddressClone();
    address.state = state;
    address.county = undefined;
    address.countyVerbose = undefined;
    const _this = this;
    this.setState({ address: address }, function () {
      _this.addressChanged(address);
      if (SettingsUtils.getCountryCode(_this.props.company) === 'US' && state) {
        _this.fetchCountiesForStateName(state);
      } else {
        _this.setState({ countyOptions: [] });
      }
    });
  }

  updateZip(zip) {
    let address = this.getAddressClone();
    address.zip = zip;

    this.setState({ address: address });
    this.addressChanged(address);
  }

  getAddressClone() {
    if (this.state.address === undefined) {
      return {};
    }
    return JSON.parse(JSON.stringify(this.state.address));
  }

  addressChanged(address) {
    let currentAddress = address !== undefined ? address : this.state.address;
    let valid = this.validateForm();
    if (this.props.addressChanged !== undefined) {
      this.props.addressChanged(valid, currentAddress);
    }
  }

  validateForm() {
    let valid = false;
    if ($(`#${this.props.id}`).form('is valid')) {
      valid = true;
    }

    return valid;
  }

  getAddressLine1Value() {
    if (
      this.props.entity !== undefined &&
      this.state.address !== undefined &&
      this.state.address.addressLine1 !== undefined
    ) {
      return this.state.address.addressLine1;
    } else {
      return '';
    }
  }

  getAddressLine2Value() {
    if (
      this.props.entity !== undefined &&
      this.state.address !== undefined &&
      this.state.address.addressLine2 !== undefined
    ) {
      return this.state.address.addressLine2;
    } else {
      return '';
    }
  }

  getAddressCityValue() {
    if (this.props.entity !== undefined && this.state.address !== undefined && this.state.address.city !== undefined) {
      return this.state.address.city;
    } else {
      return '';
    }
  }

  getAddressStateValue() {
    if (this.props.entity !== undefined && this.state.address !== undefined && this.state.address.state !== undefined) {
      return this.state.address.state;
    } else {
      return '';
    }
  }

  getAddressZipValue() {
    if (this.props.entity !== undefined && this.state.address !== undefined && this.state.address.zip !== undefined) {
      return this.state.address.zip;
    } else {
      return '';
    }
  }

  getAddressCountyValue() {
    if (this.state.address !== undefined && this.state.address.county !== undefined) {
      return this.state.address.county;
    }
    return '';
  }

  zipChanged() {
    let countryCode = SettingsUtils.getCountryCode(this.props.company);

    let zip = '';
    if (this.refs.zip !== undefined) {
      zip = this.refs.zip.value;
    }
    if (this.isAddressZipValid(countryCode, zip)) {
      this.getAddressFromZip();
    }

    if (countryCode === 'US') {
      let cleanZip = zip.replace(/\s/g, '');
      if (cleanZip.length > 5) {
        cleanZip = cleanZip.slice(0, 5);
      }
      if (zip !== cleanZip) {
        zip = cleanZip;
        this.refs.zip.value = zip;
      }
    } else if (countryCode === 'CA') {
      let cleanZip = zip;
      if (cleanZip.length > 5) {
        cleanZip = cleanZip.slice(0, 7);
      }
      if (zip !== cleanZip) {
        zip = cleanZip;
        this.refs.zip.value = zip;
      }
    }

    this.updateZip(zip);
  }

  isAddressZipValid(countryCode, zip) {
    if (countryCode === 'US') {
      try {
        let valid = true;
        if (zip === undefined || zip === '' || zip === 'undefined' || zip.length !== 5) {
          valid = false;
        }
        return valid;
      } catch (e) {
        console.error(e);
        return false;
      }
    } else {
      try {
        let valid = true;
        if (zip === undefined || zip === '' || zip === 'undefined' || zip.length !== 7) {
          valid = false;
        }
        return valid;
      } catch (e) {
        console.error(e);
        return false;
      }
    }
  }

  getAddressFromZip() {
    let _this = this;
    if (!isDocketGeoConfigured()) {
      return;
    }
    let zip = this.refs.zip.value;
    let address1 = this.refs.address1.value;
    fetchDocketZipGeocode({ address: address1, zip: zip }, this.props.company, undefined)
      .then(function (resolved) {
        if (!resolved) {
          return;
        }
        let nextAddress = _this.getAddressClone();
        if (resolved.location) {
          nextAddress.location = resolved.location;
        }
        nextAddress.city = resolved.city;
        nextAddress.county = resolved.county;
        nextAddress.countyVerbose = resolved.countyVerbose;
        nextAddress.state = resolved.state;
        nextAddress.zip = zip;
        _this.setState({ address: nextAddress });
        _this.addressChanged(nextAddress);
        $(`#${_this.props.id} .stateDropdown`).dropdown('set selected', resolved.state, undefined);
        if (SettingsUtils.getCountryCode(_this.props.company) === 'US' && resolved.state) {
          _this.fetchCountiesForStateName(resolved.state);
        }
      })
      .catch(function (error) {
        console.log(error);
      });
  }

  render() {
    let countryCode = SettingsUtils.getCountryCode(this.props.company);
    let zipPlaceholder = 'Zip Code';

    let streetLabel = <label>Street</label>;
    let cityLabel = <label>City</label>;
    let stateLabel = <label>State</label>;
    let provinceLabel = <label>Province</label>;
    let zipLabel = <label>Zip Code</label>;
    let countyLabel = <label>County</label>;
    if (countryCode === 'CA') {
      zipPlaceholder = 'Postal Code';
      zipLabel = <label>Postal Code</label>;
    }

    if (!this.props.showLabels) {
      streetLabel = undefined;
      cityLabel = undefined;
      stateLabel = undefined;
      zipLabel = undefined;
      countyLabel = undefined;
    }

    let stateField = undefined;
    if (countryCode === 'US') {
      stateField = (
        <div className="six wide field">
          {stateLabel}
          <select ref="state" name="address-level1" className="ui fluid dropdown stateDropdown">
            <option value="">State</option>
            <option value="Alabama">Alabama</option>
            <option value="Alaska">Alaska</option>
            <option value="Arizona">Arizona</option>
            <option value="Arkansas">Arkansas</option>
            <option value="California">California</option>
            <option value="Colorado">Colorado</option>
            <option value="Connecticut">Connecticut</option>
            <option value="Delaware">Delaware</option>
            <option value="District Of Columbia">District Of Columbia</option>
            <option value="Florida">Florida</option>
            <option value="Georgia">Georgia</option>
            <option value="Hawaii">Hawaii</option>
            <option value="Idaho">Idaho</option>
            <option value="Illinois">Illinois</option>
            <option value="Indiana">Indiana</option>
            <option value="Iowa">Iowa</option>
            <option value="Kansas">Kansas</option>
            <option value="Kentucky">Kentucky</option>
            <option value="Louisiana">Louisiana</option>
            <option value="Maine">Maine</option>
            <option value="Maryland">Maryland</option>
            <option value="Massachusetts">Massachusetts</option>
            <option value="Michigan">Michigan</option>
            <option value="Minnesota">Minnesota</option>
            <option value="Mississippi">Mississippi</option>
            <option value="Missouri">Missouri</option>
            <option value="Montana">Montana</option>
            <option value="Nebraska">Nebraska</option>
            <option value="Nevada">Nevada</option>
            <option value="New Hampshire">New Hampshire</option>
            <option value="New Jersey">New Jersey</option>
            <option value="New Mexico">New Mexico</option>
            <option value="New York">New York</option>
            <option value="North Carolina">North Carolina</option>
            <option value="North Dakota">North Dakota</option>
            <option value="Ohio">Ohio</option>
            <option value="Oklahoma">Oklahoma</option>
            <option value="Oregon">Oregon</option>
            <option value="Pennsylvania">Pennsylvania</option>
            <option value="Puerto Rico">Puerto Rico</option>
            <option value="Rhode Island">Rhode Island</option>
            <option value="South Carolina">South Carolina</option>
            <option value="South Dakota">South Dakota</option>
            <option value="Tennessee">Tennessee</option>
            <option value="Texas">Texas</option>
            <option value="Utah">Utah</option>
            <option value="Vermont">Vermont</option>
            <option value="Virginia">Virginia</option>
            <option value="Washington">Washington</option>
            <option value="West Virginia">West Virginia</option>
            <option value="Wisconsin">Wisconsin</option>
            <option value="Wyoming">Wyoming</option>
          </select>
        </div>
      );
    }

    let provinceField = undefined;
    if (countryCode === 'CA') {
      provinceField = (
        <div className="six wide field">
          {provinceLabel}
          <select ref="state" name="address-level1" className="ui fluid dropdown stateDropdown">
            <option value="">Province</option>
            <option value="Alberta">Alberta</option>
            <option value="British Columbia">British Columbia</option>
            <option value="Manitoba">Manitoba</option>
            <option value="New Brunswick">New Brunswick</option>
            <option value="Newfoundland and Labrador">Newfoundland and Labrador</option>
            <option value="Northwest Territories">Northwest Territories</option>
            <option value="Nova Scotia">Nova Scotia</option>
            <option value="Nunavut">Nunavut</option>
            <option value="Ontario">Ontario</option>
            <option value="Prince Edward Island">Prince Edward Island</option>
            <option value="Quebec">Quebec</option>
            <option value="Saskatchewan">Saskatchewan</option>
            <option value="Yukon">Yukon</option>
          </select>
        </div>
      );
    }

    return (
      <div className="" style={{ margin: '1em 0em' }} id={this.props.id}>
        <div className="field">
          {streetLabel}
          <div
            style={{ position: 'relative' }}
            role={isDocketGeoConfigured() ? 'combobox' : undefined}
            aria-expanded={isDocketGeoConfigured() ? this.state.suggestionsOpen : undefined}
            aria-controls={
              isDocketGeoConfigured() && this.state.suggestionsOpen && this.state.addressSuggestions.length > 0
                ? `${this.props.id}-address-suggestions`
                : undefined
            }
            aria-haspopup={isDocketGeoConfigured() ? 'listbox' : undefined}
          >
            <input
              ref="address1"
              type="text"
              name="address1"
              placeholder="Street Address Line 1"
              autoComplete={this.props.autoComplete ? this.props.autoComplete : 'on'}
              spellCheck="false"
              value={this.getAddressLine1Value()}
              style={
                this.state.suggestionsLoading
                  ? { paddingRight: '2.25rem', boxSizing: 'border-box' }
                  : { boxSizing: 'border-box' }
              }
              onChange={() => {
                this.updateAddressLine1(this.refs.address1.value);
              }}
              onFocus={() => {
                this.handleAddress1Focus();
              }}
              onBlur={() => {
                this.handleAddress1Blur();
              }}
              onKeyDown={(e) => {
                this.handleStreetKeyDown(e);
              }}
              aria-autocomplete={isDocketGeoConfigured() ? 'list' : undefined}
              aria-busy={this.state.suggestionsLoading ? 'true' : 'false'}
            />
            {this.state.suggestionsLoading ? (
              <span
                style={{
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  right: '0.5rem',
                  width: '1.75rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  pointerEvents: 'none',
                  zIndex: 2,
                }}
                title="Searching addresses"
              >
                <Icon name="circle notch" loading size="small" style={{ margin: 0, color: '#767676' }} />
              </span>
            ) : undefined}
            {this.state.suggestionsOpen && this.state.addressSuggestions.length > 0 ? (
              <ul
                id={`${this.props.id}-address-suggestions`}
                role="listbox"
                style={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  top: '100%',
                  marginTop: 2,
                  zIndex: 50,
                  maxHeight: 240,
                  overflowY: 'auto',
                  background: '#fff',
                  border: '1px solid rgba(34,36,38,.15)',
                  borderRadius: 4,
                  boxShadow: '0 2px 4px rgba(0,0,0,.08)',
                  listStyle: 'none',
                  padding: 0,
                }}
                onMouseDown={(e) => {
                  e.preventDefault();
                }}
              >
                {this.state.addressSuggestions.map((p, idx) => {
                  let main = p.description;
                  let sub = '';
                  if (p.structured_formatting !== undefined && p.structured_formatting !== null) {
                    if (p.structured_formatting.main_text !== undefined) {
                      main = p.structured_formatting.main_text;
                    }
                    if (p.structured_formatting.secondary_text !== undefined) {
                      sub = p.structured_formatting.secondary_text;
                    }
                  }
                  let active = idx === this.state.suggestionHighlight;
                  return (
                    <li
                      key={p.place_id}
                      role="option"
                      aria-selected={active}
                      style={{
                        padding: '0.55em 0.75em',
                        cursor: 'pointer',
                        background: active ? 'rgba(0,0,0,.04)' : undefined,
                        borderBottom: '1px solid rgba(34,36,38,.08)',
                      }}
                      onMouseEnter={() => {
                        this.setState({ suggestionHighlight: idx });
                      }}
                      onClick={() => {
                        this.handleSelectPrediction(p);
                      }}
                    >
                      <div style={{ fontWeight: 500 }}>{main}</div>
                      {sub ? <div style={{ fontSize: '0.85em', color: '#767676' }}>{sub}</div> : undefined}
                    </li>
                  );
                })}
              </ul>
            ) : undefined}
          </div>
        </div>
        {this.props.hideSecondLine ? undefined : (
          <div className="field">
            <input
              ref="address2"
              type="text"
              name="address2"
              placeholder="Street Address Line 2"
              autoComplete={this.props.autoComplete ? this.props.autoComplete : 'on'}
              spellCheck="false"
              value={this.getAddressLine2Value()}
              onChange={() => {
                this.updateAddressLine2(this.refs.address2.value);
              }}
            />
          </div>
        )}
        <div className="three fields" style={{ flexFlow: isMobileOnly ? '' : 'nowrap' }}>
          <div className="four wide field">
            {zipLabel}
            <input
              ref="zip"
              type="text"
              name="zip"
              placeholder={zipPlaceholder}
              autoComplete={this.props.autoComplete ? this.props.autoComplete : 'on'}
              value={this.getAddressZipValue()}
              onChange={() => {
                this.zipChanged();
              }}
            />
          </div>
          <div className="six wide field">
            {cityLabel}
            <input
              ref="city"
              type="text"
              name="city"
              placeholder="City"
              autoComplete={this.props.autoComplete ? this.props.autoComplete : 'on'}
              spellCheck="false"
              value={this.getAddressCityValue()}
              onChange={() => {
                this.updateCity(this.refs.city.value);
              }}
            />
          </div>
          {stateField}
          {provinceField}
        </div>
        {countryCode === 'US' ? (
          <div className="field">
            {countyLabel}
            <Dropdown
              selection
              search
              fluid
              placeholder="Choose County"
              disabled={!this.getAddressStateValue()}
              options={this.state.countyOptions}
              value={this.getAddressCountyValue() || undefined}
              onChange={(e, { value }) => {
                const countyVerbose = value
                  ? String(value).endsWith(' County')
                    ? String(value)
                    : `${value} County`
                  : undefined;
                this.updateCounty(value, countyVerbose);
              }}
              style={{ fontWeight: '300' }}
            />
          </div>
        ) : undefined}
      </div>
    );
  }
}

AddressForm.propTypes = {
  id: PropTypes.string,
  entity: PropTypes.object,
  company: PropTypes.object,
  showLabels: PropTypes.bool,
  addressChanged: PropTypes.func,
  hideSecondLine: PropTypes.bool,
  autoComplete: PropTypes.string,
  biasLocation: PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number,
  }),
};

export default AddressForm;
